/**
 * API Keys Page
 *
 * Manage API keys for programmatic access:
 * - View active and revoked keys
 * - Create new API keys
 * - Revoke and rotate keys
 *
 * Reference migration onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`.
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Text,
  Card,
  CardHeader,
  Button,
  Badge,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tooltip,
} from '@fluentui/react-components';
import {
  KeyRegular,
  AddRegular,
  CopyRegular,
  DeleteRegular,
  ArrowSyncRegular,
  CheckmarkRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeysApi } from '../services/api';
import type { ApiKey, ApiKeyScope, CreateApiKeyRequest } from '../types';
import { useAuth } from '../features/authentication';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

/** Roles allowed to manage API keys (create / rotate / revoke) — mirrors `ApiKeysController.RequireAnyRole`. */
const API_KEY_MANAGER_ROLES = ['Organization.Admin', 'TenantAdmin', 'ApiKeyManager'];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  monoText: {
    fontFamily: 'monospace',
  },
  keyDisplay: {
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  keyValue: {
    fontFamily: 'monospace',
    userSelect: 'all',
    wordBreak: 'break-all',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  scopeCheckboxes: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  scopesCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  usageCardBody: {
    padding: tokens.spacingHorizontalM,
  },
});

/** Wraps the (unpaged) `apiKeysApi.getAll` response as a `PagedResult` — `useAdaptiveGridData` then
 *  applies search/sort/paging in memory (client mode), since the API key list is never large
 *  enough to warrant a real server-paged endpoint. */
async function fetchApiKeysPage(
  includeRevoked: boolean,
  params: ListQueryParams
): Promise<PagedResult<ApiKey>> {
  const items = await apiKeysApi.getAll(includeRevoked);
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const crumbs: Crumb[] = [{ label: 'API Keys' }];

export const ApiKeysPage: FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManageApiKeys = user?.roles?.some((role) => API_KEY_MANAGER_ROLES.includes(role)) ?? false;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<ApiKeyScope[]>(['Read']);
  const [newKeyExpiresIn, setNewKeyExpiresIn] = useState('90');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'name:asc' });

  // "Show Revoked" is a boolean scope toggle — encoded into the shared `filters` URL param
  // (§17/§19) rather than extending `useListUrlState` with a bespoke boolean slot.
  const showRevoked = state.filters === 'includeRevoked:eq:true';
  const handleShowRevokedChange = (value: boolean) => setFilters(value ? 'includeRevoked:eq:true' : '');

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<ApiKey>({
    queryKeyBase: ['api-keys', showRevoked],
    fetchPage: (params) => fetchApiKeysPage(showRevoked, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['name', 'keyPrefix'],
    sortState,
  });

  const {
    items: apiKeys,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after revoke/search/filter.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: (request: CreateApiKeyRequest) => apiKeysApi.create(request),
    onSuccess: (response) => {
      setNewlyCreatedKey(response.plainTextKey);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setIsCreateDialogOpen(false);
      setNewKeyName('');
      setNewKeyScopes(['Read']);
    },
  });

  // Revoke API key mutation
  const revokeKeyMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => apiKeysApi.revoke(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  // Rotate API key mutation
  const rotateKeyMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.rotate(id),
    onSuccess: (response) => {
      setNewlyCreatedKey(response.plainTextKey);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCreateKey = () => {
    const expiresAt = newKeyExpiresIn
      ? new Date(Date.now() + parseInt(newKeyExpiresIn) * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

    createKeyMutation.mutate({
      name: newKeyName,
      scopes: newKeyScopes,
      expiresAt,
    });
  };

  const handleCopyKey = async () => {
    if (newlyCreatedKey) {
      await navigator.clipboard.writeText(newlyCreatedKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const toggleScope = (scope: ApiKeyScope) => {
    if (newKeyScopes.includes(scope)) {
      setNewKeyScopes(newKeyScopes.filter((s) => s !== scope));
    } else {
      setNewKeyScopes([...newKeyScopes, scope]);
    }
  };

  const actionButtonCount = canManageApiKeys ? 2 : 0;
  const columnCount = canManageApiKeys ? 7 : 6;

  const createKeyDialog = (
    <Dialog open={isCreateDialogOpen} onOpenChange={(_, data) => setIsCreateDialogOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="primary" icon={<AddRegular />}>
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogSurface>
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div className={styles.form}>
              <Field label="Key Name" required>
                <Input
                  value={newKeyName}
                  onChange={(_, data) => setNewKeyName(data.value)}
                  placeholder="e.g., CI/CD Pipeline"
                />
              </Field>

              <Field label="Permissions">
                <div className={styles.scopeCheckboxes}>
                  <Checkbox
                    label="Read - View findings, vulnerabilities, and reports"
                    checked={newKeyScopes.includes('Read')}
                    onChange={() => toggleScope('Read')}
                  />
                  <Checkbox
                    label="Write - Update findings, trigger scans"
                    checked={newKeyScopes.includes('Write')}
                    onChange={() => toggleScope('Write')}
                  />
                  <Checkbox
                    label="Admin - Full access including user management"
                    checked={newKeyScopes.includes('Admin')}
                    onChange={() => toggleScope('Admin')}
                  />
                </div>
              </Field>

              <Field label="Expires In (days)" hint="Leave empty for no expiration">
                <Input
                  type="number"
                  value={newKeyExpiresIn}
                  onChange={(_, data) => setNewKeyExpiresIn(data.value)}
                  placeholder="90"
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleCreateKey}
              disabled={!newKeyName || newKeyScopes.length === 0 || createKeyMutation.isPending}
            >
              {createKeyMutation.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load API keys." onRetry={refetch} />;
  } else if (apiKeys.length === 0) {
    listBody = (
      <EmptyState
        icon={<KeyRegular />}
        title="No API Keys"
        description="Create an API key to integrate with LeftShield programmatically."
        actionLabel={canManageApiKeys ? 'Create API Key' : undefined}
        onAction={canManageApiKeys ? () => setIsCreateDialogOpen(true) : undefined}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="API keys">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Key Prefix</TableHeaderCell>
              <TableHeaderCell>Scopes</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'lastUsedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('lastUsedAt')}
                onClick={() => toggleSort('lastUsedAt')}
              >
                Last Used
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'expiresAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('expiresAt')}
                onClick={() => toggleSort('expiresAt')}
              >
                Expires
              </TableHeaderCell>
              {canManageApiKeys && (
                <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                  Actions
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((item) => {
              const isRotating = rotateKeyMutation.isPending && rotateKeyMutation.variables === item.id;
              const isRevoking =
                revokeKeyMutation.isPending && revokeKeyMutation.variables?.id === item.id;

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Text weight="semibold">{item.name}</Text>
                  </TableCell>
                  <TableCell>
                    <Text className={styles.monoText}>{item.keyPrefix}...</Text>
                  </TableCell>
                  <TableCell>
                    <div className={styles.scopesCell}>
                      {item.scopes.map((scope) => (
                        <Badge key={scope} appearance="outline" size="medium">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.isRevoked ? (
                      <Badge appearance="filled" color="danger">
                        Revoked
                      </Badge>
                    ) : item.expiresAt && new Date(item.expiresAt) < new Date() ? (
                      <Badge appearance="filled" color="warning">
                        Expired
                      </Badge>
                    ) : (
                      <Badge appearance="filled" color="success">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {item.lastUsedAt ? new Date(item.lastUsedAt).toLocaleDateString() : 'Never'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : 'Never'}
                    </Text>
                  </TableCell>
                  {canManageApiKeys && (
                    <TableCell>
                      {/* Hidden-not-disabled: only active keys expose Rotate/Revoke; revoked keys render no actions. */}
                      {!item.isRevoked && (
                        <div className={styles.actionsCell}>
                          <Tooltip content="Rotate Key" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<ArrowSyncRegular />}
                              size="small"
                              aria-label="Rotate Key"
                              disabled={isRotating}
                              onClick={() => rotateKeyMutation.mutate(item.id)}
                            />
                          </Tooltip>
                          <Tooltip content="Revoke Key" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<DeleteRegular />}
                              size="small"
                              aria-label="Revoke Key"
                              disabled={isRevoking}
                              onClick={() =>
                                revokeKeyMutation.mutate({ id: item.id, reason: 'Manual revocation' })
                              }
                            />
                          </Tooltip>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.listSection}>
        <ListPageLayout
          header={<MasterDetailHeader crumbs={crumbs} busy={isFetching && !isLoading} />}
          toolbar={
            <>
              {newlyCreatedKey && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    <MessageBarTitle>API Key Created Successfully</MessageBarTitle>
                    <Text>Copy this key now. You won&apos;t be able to see it again.</Text>
                    <div className={styles.keyDisplay}>
                      <Text className={styles.keyValue}>{newlyCreatedKey}</Text>
                      <Tooltip content={copiedKey ? 'Copied!' : 'Copy to clipboard'} relationship="label">
                        <Button
                          appearance="subtle"
                          icon={copiedKey ? <CheckmarkRegular /> : <CopyRegular />}
                          onClick={handleCopyKey}
                        />
                      </Tooltip>
                    </div>
                  </MessageBarBody>
                </MessageBar>
              )}
              <ListToolbar
                primaryActions={canManageApiKeys ? createKeyDialog : undefined}
                search={state.search}
                onSearchChange={setSearch}
                searchPlaceholder="Search API keys…"
                showDeleted={showRevoked}
                onShowDeletedChange={handleShowRevokedChange}
                showDeletedLabel="Show Revoked"
                onRefresh={refetch}
                isRefreshing={isFetching}
                testIdPrefix="api-keys"
              />
            </>
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="api-keys-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      {/* Usage Information — static reference card, outside the scrolling list frame. */}
      <Card>
        <CardHeader
          header={<Text weight="semibold">API Key Usage</Text>}
          description="Include your API key in the Authorization header"
        />
        <div className={styles.usageCardBody}>
          <div className={styles.keyDisplay}>
            <Text className={styles.monoText}>Authorization: Bearer YOUR_API_KEY</Text>
          </div>
        </div>
      </Card>
    </div>
  );
};
